import { useCallback, useMemo } from 'react';

import { MinimalCartEntryType } from 'features/cart/types';
import { useStoreContext } from 'features/store';

import {
  LoyaltyServiceOfferRedemptionType,
  LoyaltyServiceServiceMode,
  useLoyaltyServiceAnonymousOffersQuery,
  useLoyaltyServiceOffersQuery,
} from 'generated/graphql-gateway';
import { selectors, useAppSelector } from 'state/global-state';
import { useLoyaltyUserId } from 'state/loyalty/hooks/use-loyalty-user';
import {
  ICartEntryType,
  useInRestaurantRedemptionContext,
} from 'state/loyalty/in-restaurant-redemption';
import {
  AppOffer,
  AppOfferListItem,
  AppPersonalizedOfferGroup,
  isAppPersonalizedOfferGroup,
} from 'state/loyalty/types';
import { useServiceModeContext } from 'state/service-mode';
import noop from 'utils/noop';

import { ServiceOfferItem } from '../types';
import { mapServiceOfferItem } from '../utils';

const SERVICE_MODE_MAP: Record<string, LoyaltyServiceServiceMode | undefined> = {
  CURBSIDE: LoyaltyServiceServiceMode.CURBSIDE,
  DELIVERY: LoyaltyServiceServiceMode.DELIVERY,
  CATERING_DELIVERY: LoyaltyServiceServiceMode.DELIVERY,
  DRIVE_THRU: LoyaltyServiceServiceMode.DRIVE_THRU,
  MOBILE_ORDER_DRIVE_THRU: LoyaltyServiceServiceMode.DRIVE_THRU,
  EAT_IN: LoyaltyServiceServiceMode.EAT_IN,
  TABLE_SERVICE: LoyaltyServiceServiceMode.EAT_IN,
  TAKEOUT: LoyaltyServiceServiceMode.TAKEOUT,
  CATERING_PICKUP: LoyaltyServiceServiceMode.TAKEOUT,
};

function isAppOffer(item: AppOfferListItem): item is AppOffer {
  return (
    (item as any).__apptype === 'AppSystemwideOffer' ||
    (item as any).__apptype === 'AppPersonalizedOffer'
  );
}

export function useLoyaltyServiceOffers() {
  const { serviceMode } = useServiceModeContext();
  const { store } = useStoreContext();
  const storeId = store?.number;
  const { loyaltyUserId, loading: loyaltyUserIdLoading } = useLoyaltyUserId();

  const appliedOffers = useAppSelector(selectors.loyalty.selectAppliedOffers);
  const minimalCartEntries = useAppSelector(selectors.ordering.selectMinimalCartEntries);
  const { inRestaurantRedemptionCart } = useInRestaurantRedemptionContext();

  const loyaltyServiceMode = serviceMode ? SERVICE_MODE_MAP[serviceMode] : undefined;
  const variables = {
    serviceMode: loyaltyServiceMode,
    storeId,
    redemptionTypes: [LoyaltyServiceOfferRedemptionType.STANDARD],
    includeOfferGroups: true,
    omitInvalids: false,
  };

  const {
    data: loyaltyServiceOffersData,
    loading: loyaltyServiceOffersLoading,
    refetch: refetchLoyaltyServiceOffers,
  } = useLoyaltyServiceOffersQuery({
    variables,
    skip: loyaltyUserIdLoading || !loyaltyUserId,
    fetchPolicy: 'network-only',
    errorPolicy: 'all',
  });

  const {
    data: loyaltyServiceAnonymousOffersData,
    loading: loyaltyServiceAnonymousOffersLoading,
    refetch: refetchLoyaltyServiceAnonymousOffers,
  } = useLoyaltyServiceAnonymousOffersQuery({
    variables,
    skip: loyaltyUserIdLoading || Boolean(loyaltyUserId),
    fetchPolicy: 'network-only',
    errorPolicy: 'all',
  });

  const loading =
    loyaltyUserIdLoading ||
    (loyaltyUserId ? loyaltyServiceOffersLoading : loyaltyServiceAnonymousOffersLoading);

  const appliedOfferIds = useMemo(() => {
    const ids = new Set(appliedOffers.map(o => o.id ?? ''));
    minimalCartEntries.forEach(entry => {
      if (
        (entry.type === MinimalCartEntryType.discount ||
          entry.type === MinimalCartEntryType.incentive) &&
        entry.referenceId
      ) {
        ids.add(entry.referenceId);
      }
    });
    return ids;
  }, [appliedOffers, minimalCartEntries]);

  const inRestaurantRedemptionIds = useMemo(() => {
    return new Set(
      inRestaurantRedemptionCart
        .filter(entry => entry.type === ICartEntryType.OFFER)
        .map(entry => (entry as any).details?.offer?._id)
        .filter(Boolean)
    );
  }, [inRestaurantRedemptionCart]);

  const { offerListItems, offersInCart, personalizedOfferGroups } = useMemo(() => {
    const items = loyaltyUserId
      ? loyaltyServiceOffersData?.loyaltyServiceOffers?.items
      : loyaltyServiceAnonymousOffersData?.loyaltyServiceAnonymousOffers?.items;
    if (!items?.length) {
      return {
        offerListItems: [] as AppOfferListItem[],
        offersInCart: [] as AppOffer[],
        personalizedOfferGroups: [] as AppPersonalizedOfferGroup[],
      };
    }

    const mapped = items.map(item => mapServiceOfferItem(item as ServiceOfferItem));
    const list: AppOfferListItem[] = [];
    const cart: AppOffer[] = [];
    const pGroups: AppPersonalizedOfferGroup[] = [];

    for (const item of mapped) {
      if (isAppOffer(item)) {
        const inCart =
          appliedOfferIds.has(item.loyaltyEngineId) || inRestaurantRedemptionIds.has(item.cmsId);
        if (inCart) {
          cart.push(item);
        } else {
          list.push(item);
        }
      } else {
        list.push(item);
        if (isAppPersonalizedOfferGroup(item)) {
          pGroups.push(item);
        }
      }
    }

    return { offerListItems: list, offersInCart: cart, personalizedOfferGroups: pGroups };
  }, [
    appliedOfferIds,
    inRestaurantRedemptionIds,
    loyaltyServiceAnonymousOffersData,
    loyaltyServiceOffersData,
    loyaltyUserId,
  ]);

  const refreshOffers = useCallback(() => {
    if (loyaltyUserIdLoading) {
      return;
    }
    if (loyaltyUserId) {
      refetchLoyaltyServiceOffers();
      return;
    }
    refetchLoyaltyServiceAnonymousOffers();
  }, [
    loyaltyUserId,
    loyaltyUserIdLoading,
    refetchLoyaltyServiceAnonymousOffers,
    refetchLoyaltyServiceOffers,
  ]);

  const reprocessOffers = useCallback(noop, []);

  return {
    offerListItems,
    offersInCart,
    personalizedOfferGroups,
    reprocessOffers,
    refreshOffers,
    loading,
    baseOffersLoading: loading,
  };
}
